import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Mail, Lock, User, Eye, EyeOff, Loader2, Sparkles, ShieldCheck, Info, Tag, ArrowLeft } from 'lucide-react';

const Logo = () => (
  <div className="relative w-10 h-10 mx-auto">
    <div className="absolute inset-0 rounded-lg" style={{ background: 'linear-gradient(135deg, #8247E5, #A855F7)', boxShadow: '0 4px 14px rgba(130,71,229,0.35)' }} />
    <div className="absolute inset-0 flex items-center justify-center">
      <svg viewBox="0 0 40 40" className="w-5 h-5" fill="none">
        <path d="M12 8 L20 4 L28 8 L28 16 L20 20 L12 16 Z" fill="white" opacity="0.95" />
        <path d="M12 24 L20 20 L28 24 L28 32 L20 36 L12 32 Z" fill="white" opacity="0.7" />
      </svg>
    </div>
  </div>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

const AuthModal = ({ open, onOpenChange, initialMode = 'login' }) => {
  const { login, signupRequest, signupVerify, signupResend, loginWithGoogle, googleEnabled, googleClientId } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [refFromLink, setRefFromLink] = useState(false);
  // OTP verification state
  const [pendingId, setPendingId] = useState(null);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);   // seconds remaining
  const [resendIn, setResendIn] = useState(0);           // cooldown seconds
  const { toast } = useToast();
  const googleBtnRef = useRef(null);
  const otpInputRefs = useRef([]);

  useEffect(() => { setMode(initialMode); }, [initialMode, open]);
  useEffect(() => { if (!open) { setError(''); setPassword(''); setPendingId(null); setOtpDigits(['','','','','','']); } }, [open]);

  // Countdown timers for OTP expiry + resend
  useEffect(() => {
    if (mode !== 'verify' || !pendingId) return;
    const iv = setInterval(() => {
      setOtpExpiresIn((s) => Math.max(0, s - 1));
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(iv);
  }, [mode, pendingId]);

  // Auto-focus first OTP box when entering verify mode + cleanup Google button
  useEffect(() => {
    if (mode === 'verify') {
      // Google Sign-In sometimes leaves an iframe in the DOM even after unmount.
      // Force-clear the ref div to be safe.
      if (googleBtnRef.current) googleBtnRef.current.innerHTML = '';
      if (otpInputRefs.current[0]) otpInputRefs.current[0].focus();
    }
  }, [mode]);

  // Pick up ?ref= from the URL / localStorage the first time the modal opens.
  // Users landing via a referral link get their friend's code pre-filled.
  useEffect(() => {
    if (!open) return;
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get('ref') || localStorage.getItem('tokengate_ref') || '';
      if (ref) {
        setReferralCode(ref.toUpperCase());
        setRefFromLink(true);
        localStorage.setItem('tokengate_ref', ref);
      }
    } catch {}
  }, [open]);

  // Load Google Identity Services script and render button once modal opens.
  // Skip in verify mode — the panel is unmounted and the Google iframe would leak.
  useEffect(() => {
    if (!open || !googleEnabled || !googleClientId) return;
    if (mode === 'verify') return;
    const src = 'https://accounts.google.com/gsi/client';
    let script = document.querySelector(`script[src="${src}"]`);
    const render = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          try {
            setSubmitting(true);
            await loginWithGoogle(response.credential, mode === 'signup' ? referralCode : null);
            toast({ title: 'Welcome!' });
            localStorage.removeItem('tokengate_ref');
            onOpenChange(false);
          } catch (e) {
            setError(e?.response?.data?.detail || 'Google sign-in failed');
          } finally { setSubmitting(false); }
        },
      });
      // Clear previous button then render
      googleBtnRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline', size: 'large', width: 360, shape: 'rectangular', text: mode === 'signup' ? 'signup_with' : 'signin_with',
      });
    };
    if (!script) {
      script = document.createElement('script');
      script.src = src; script.async = true; script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    } else if (window.google) {
      render();
    } else {
      script.addEventListener('load', render);
    }
    // eslint-disable-next-line
  }, [open, googleEnabled, googleClientId, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please fill in every field'); return; }
    if (mode === 'signup' && !name.trim()) { setError('Please tell us your name'); return; }
    if (mode === 'signup' && password.length < 6) { setError('Password should be at least 6 characters'); return; }
    try {
      setSubmitting(true);
      if (mode === 'signup') {
        // Step 1: request OTP
        const res = await signupRequest(email, password, name.trim(), referralCode.trim() || null);
        setPendingId(res.pending_id);
        setOtpExpiresIn(res.otp_expires_in || 600);
        setResendIn(res.resend_available_in || 30);
        setMode('verify');
        toast({ title: 'Check your inbox', description: `We sent a 6-digit code to ${email}` });
      } else {
        await login(email, password);
        toast({ title: 'Welcome back!' });
        onOpenChange(false);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  const setOtpDigit = (idx, val) => {
    const clean = val.replace(/\D/g, '').slice(-1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[idx] = clean;
      return next;
    });
    if (clean && idx < 5) otpInputRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpInputRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.padEnd(6, '').split('');
    setOtpDigits(next);
    const target = Math.min(pasted.length, 5);
    otpInputRefs.current[target]?.focus();
    if (pasted.length === 6) {
      // Auto-submit
      setTimeout(() => verifyOtp(pasted), 100);
    }
  };

  const verifyOtp = async (overrideOtp) => {
    setError('');
    const otp = overrideOtp || otpDigits.join('');
    if (otp.length !== 6) { setError('Enter all 6 digits'); return; }
    try {
      setSubmitting(true);
      await signupVerify(pendingId, otp);
      toast({ title: 'Account verified!', description: referralCode ? `You'll get a 10% welcome discount by email.` : 'Welcome to TokenGate!' });
      localStorage.removeItem('tokengate_ref');
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invalid or expired code.');
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally { setSubmitting(false); }
  };

  const resendOtp = async () => {
    if (resendIn > 0 || !pendingId) return;
    setError('');
    try {
      const res = await signupResend(pendingId);
      setOtpExpiresIn(res.otp_expires_in || 600);
      setResendIn(res.resend_available_in || 30);
      toast({ title: 'New code sent', description: `Check ${email} for the new 6-digit code.` });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not resend code.');
    }
  };

  const goBackToSignup = () => {
    setMode('signup');
    setPendingId(null);
    setOtpDigits(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="relative px-8 pt-8 pb-6">
          <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(130,71,229,0.15), transparent 70%)' }} />
          <div className="relative text-center">
            <Logo />
            <h2 className="font-display text-2xl font-bold mt-4" data-testid="auth-modal-title">
              {mode === 'signup' ? 'Create your account' : mode === 'verify' ? 'Verify your email' : 'Welcome back'}
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              {mode === 'signup' && 'Get gift codes to your inbox and track every order.'}
              {mode === 'login' && 'Sign in to see your orders and codes.'}
              {mode === 'verify' && <>We emailed a 6-digit code to <b className="text-neutral-800">{email}</b></>}
            </p>
          </div>
        </div>

        {mode === 'verify' ? (
          <div key="panel-verify" className="px-8 pb-8 -mt-2" data-testid="auth-verify-panel">
            {/* OTP boxes */}
            <div className="flex justify-center gap-2 my-6 flex-wrap" onPaste={handleOtpPaste}>
              {otpDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpInputRefs.current[i] = el)}
                  data-testid={`otp-input-${i}`}
                  value={d}
                  onChange={(e) => setOtpDigit(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className="w-11 h-13 min-h-[52px] text-center text-2xl font-bold font-mono border-2 border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8247E5] focus:border-transparent transition-all bg-white"
                />
              ))}
            </div>

            <div className="text-center text-xs text-neutral-500 mb-4">
              {otpExpiresIn > 0
                ? <>Code expires in <span className="font-semibold text-neutral-700">{Math.floor(otpExpiresIn / 60)}:{String(otpExpiresIn % 60).padStart(2, '0')}</span></>
                : <span className="text-rose-600 font-semibold">Code expired. Please resend.</span>
              }
            </div>

            {error && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2.5 flex items-start gap-2 mb-3" data-testid="auth-verify-error">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
              </div>
            )}

            <Button
              data-testid="auth-verify-submit"
              onClick={() => verifyOtp()}
              disabled={submitting || otpDigits.join('').length !== 6}
              className="w-full h-11 bg-[#8247E5] hover:bg-[#6d33cc] text-white font-semibold">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & continue'}
            </Button>

            <div className="mt-4 text-center text-sm">
              <span className="text-neutral-500">Didn't get it? </span>
              <button
                onClick={resendOtp}
                disabled={resendIn > 0}
                data-testid="auth-verify-resend"
                className="text-[#8247E5] font-semibold hover:underline disabled:text-neutral-400 disabled:hover:no-underline disabled:cursor-not-allowed">
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>

            <button
              onClick={goBackToSignup}
              className="mt-5 mx-auto flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800"
              data-testid="auth-verify-back">
              <ArrowLeft className="w-3 h-3" /> Wrong email? Go back
            </button>

            <div className="mt-4 flex items-center justify-center gap-3 text-[11px] text-neutral-400">
              <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Encrypted</span>
              <span>Never share this code. TokenGate will never ask for it.</span>
            </div>
          </div>
        ) : (
        <div key="panel-auth" className="px-8 pb-8 -mt-2">
          {/* Google button */}
          {googleEnabled ? (
            <div ref={googleBtnRef} className="flex justify-center mb-4" />
          ) : (
            <button type="button" disabled
              title="Google Sign-In is not configured on the server"
              className="w-full h-11 rounded-lg border border-neutral-200 bg-white flex items-center justify-center gap-2 text-sm text-neutral-500 cursor-not-allowed mb-4">
              <GoogleIcon />
              <span>Continue with Google</span>
              <span className="text-[10px] bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded ml-1">Setup needed</span>
            </button>
          )}

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-2 bg-white text-neutral-400">or with email</span></div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === 'signup' && (
              <div>
                <Label className="text-xs text-neutral-600">Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" className="pl-10 h-11" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-neutral-600">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-11" autoComplete="email" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-neutral-600">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'} className="pl-10 pr-10 h-11" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {mode === 'signup' && (
              <div>
                <Label className="text-xs text-neutral-600 flex items-center gap-1">
                  <Tag className="w-3 h-3 text-[#8247E5]" /> Referral code <span className="text-neutral-400 font-normal">(optional)</span>
                </Label>
                <Input
                  data-testid="signup-referral-code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="e.g. FRIEND-XYZ"
                  className="mt-1 h-11 font-mono tracking-wider"
                  maxLength={32}
                />
                {refFromLink && referralCode && (
                  <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> A friend invited you. You'll get 10% off your first order.</p>
                )}
              </div>
            )}

            {error && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2.5 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
              </div>
            )}

            <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#8247E5] hover:bg-[#6d33cc] text-white font-semibold" data-testid="auth-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'signup' ? 'Continue' : 'Sign in')}
            </Button>
          </form>

          <div className="mt-5 text-center text-sm text-neutral-600">
            {mode === 'signup' ? (
              <>Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-[#8247E5] font-semibold hover:underline">Sign in</button>
              </>
            ) : (
              <>New to TokenGate?{' '}
                <button onClick={() => setMode('signup')} className="text-[#8247E5] font-semibold hover:underline">Create an account</button>
              </>
            )}
          </div>

          <div className="mt-4 flex items-center justify-center gap-3 text-[11px] text-neutral-400">
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Encrypted</span>
            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Guest checkout available</span>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
